/**
 * useCompanySettings - Hook for fetching and managing company settings.
 * Returns profile data, notification preferences, and mutation functions.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import {
  fetchCompanyProfile,
  updateCompanyProfile,
  uploadCompanyLogo,
  removeCompanyLogo,
  updateCompanySettings,
} from '../services/settingsService';
import { fetchMySettings } from '../services/companyService';
import type { CompanyProfile, CompanySettings } from '../types/company';

interface UseCompanySettingsReturn {
  // Data
  profile: CompanyProfile | null;
  settings: CompanySettings | null;

  // Loading states
  isLoading: boolean;
  isSaving: boolean;
  isUploadingLogo: boolean;

  // Error state
  error: string | null;

  // Actions
  refetch: () => Promise<void>;
  saveProfile: (data: Partial<CompanyProfile>) => Promise<boolean>;
  saveLogo: (blob: Blob) => Promise<boolean>;
  removeLogo: () => Promise<boolean>;
  saveSettings: (data: Partial<CompanySettings>) => Promise<boolean>;
}

export function useCompanySettings(): UseCompanySettingsReturn {
  const { session } = useAuth();
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [settings, setSettings] = useState<CompanySettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!session?.token) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch profile and settings in parallel
      const [profileData, settingsData] = await Promise.all([
        fetchCompanyProfile(session.token),
        fetchMySettings(session.token),
      ]);

      setProfile(profileData);
      setSettings(settingsData);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load settings';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [session?.token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveProfile = useCallback(async (data: Partial<CompanyProfile>): Promise<boolean> => {
    if (!session?.token) return false;

    setIsSaving(true);
    try {
      const updated = await updateCompanyProfile(session.token, data);
      setProfile(updated);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save profile';
      setError(message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [session?.token]);

  const saveLogo = useCallback(async (blob: Blob): Promise<boolean> => {
    if (!session?.token) return false;

    setIsUploadingLogo(true);
    try {
      // Convert blob to file
      const file = new File([blob], 'logo.jpg', { type: 'image/jpeg' });
      const result = await uploadCompanyLogo(session.token, file);

      // Update profile with new logo URL
      const updated = await updateCompanyProfile(session.token, { logoUrl: result.url } as Partial<CompanyProfile>);
      setProfile(updated);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to upload logo';
      setError(message);
      return false;
    } finally {
      setIsUploadingLogo(false);
    }
  }, [session?.token]);

  const removeLogo = useCallback(async (): Promise<boolean> => {
    if (!session?.token) return false;

    setIsSaving(true);
    try {
      await removeCompanyLogo(session.token);
      setProfile((prev) => prev ? { ...prev, logoUrl: undefined } : null);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove logo';
      setError(message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [session?.token]);

  const saveSettings = useCallback(async (data: Partial<CompanySettings>): Promise<boolean> => {
    if (!session?.token) return false;

    setIsSaving(true);
    try {
      const updated = await updateCompanySettings(session.token, data);
      setSettings(updated);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save settings';
      setError(message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [session?.token]);

  return {
    profile,
    settings,
    isLoading,
    isSaving,
    isUploadingLogo,
    error,
    refetch: fetchData,
    saveProfile,
    saveLogo,
    removeLogo,
    saveSettings,
  };
}
